
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Search,
  FileText,
  MoreVertical,
  Plus,
  AlertCircle,
  CheckCircle2,
  Clock,
  BarChart,
  ListTodo,
  Check,
  Edit,
  Trash2,
} from 'lucide-react';
import { CreateProjectForm } from '@/components/projects/CreateProjectForm';
import { ViewProjectDialog } from '@/components/projects/ViewProjectDialog';
import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useNavigate } from 'react-router-dom';
import { projectService } from '@/services/project.service';
import taskService from '@/services/task.service';
import { baseURL } from '@/lib/axios';
// import { Project as ApiProject } from '@/types/models'; // Not used in current implementation
import { toast } from 'sonner';
import { getStatusesByCategory } from '@/services/status.service';
import { getColorClasses } from '@/utils/statusUtils';

type ProjectStatus = string | { name?: string | null } | null;

// Project interface with populated employee data
interface Project {
  _id?: string; // MongoDB ID
  id: string;
  name: string;
  client: string;
  status: ProjectStatus;
  progress: number;
  startDate: string;
  endDate: string;
  team: Array<{
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    employeeId: string;
    profilePicture?: string | null;
  }>;
  priority: string;
  tasks: {
    total: number;
    completed: number;
    inProgress: number;
    blocked: number;
  };
  description?: string;
  createdDate?: string;
  projectManager?: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    employeeId: string;
    profilePicture?: string | null;
  };
  projectManagerId?: string;
  teamMemberIds?: string[];
  tags?: string[];
  totalLoggedHours?: number;
  hoursByDay?: Array<{ day: string; hours: number }>;
}

const apiBaseWithoutPrefix = baseURL ? baseURL.replace(/\/api\/v1\/?$/, '') : '';

const buildImageUrl = (path: string) => {
  if (!apiBaseWithoutPrefix) {
    return path;
  }
  const normalizedBase = apiBaseWithoutPrefix.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

const getProfileImageUrl = (profilePicture?: string | null) => {
  if (!profilePicture) {
    return null;
  }
  if (profilePicture.startsWith('http')) {
    return profilePicture;
  }
  return buildImageUrl(profilePicture);
};

const getInitials = (firstName?: string, lastName?: string, email?: string) => {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim();
  if (initials) {
    return initials.toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return '';
};


// Helper to extract status name from object or string and normalize to uppercase
const getStatusName = (status: any): string => {
  if (!status) return '';
  let statusValue: string;
  if (typeof status === 'object' && 'name' in status) {
    statusValue = status.name;
  } else {
    statusValue = String(status);
  }
  
  // Normalize to uppercase for consistency
  // Handle common status formats and convert to uppercase
  const statusUpper = statusValue.toUpperCase();
  const statusMap: Record<string, string> = {
    'PLANNING': 'PLANNING',
    'IN_PROGRESS': 'IN_PROGRESS',
    'IN PROGRESS': 'IN_PROGRESS',
    'ON_HOLD': 'ON_HOLD',
    'ON HOLD': 'ON_HOLD',
    'COMPLETED': 'COMPLETED',
    'COMPLETE': 'COMPLETED',
    'TESTING': 'TESTING',
    'CANCELLED': 'CANCELLED',
    'CANCELED': 'CANCELLED',
  };
  
  return statusMap[statusUpper] || statusUpper;
};

// Get status color - use API colors when available, fallback to hardcoded
const getStatusColor = (status: string | any, projectStatuses: Array<{ name: string; color: string }> = []) => {
  // Extract status name if it's an object
  const statusName = getStatusName(status);
  
  // Try to find status in API statuses first
  if (projectStatuses.length > 0) {
    const apiStatus = projectStatuses.find(s => 
      s.name.toLowerCase() === statusName.toLowerCase() ||
      statusName.toUpperCase() === s.name.toUpperCase()
    );
    if (apiStatus) {
      const colorClasses = getColorClasses(apiStatus.color);
      if (colorClasses) {
        return colorClasses;
      }
    }
  }
  
  // Fallback to hardcoded colors
  const statusUpper = statusName.toUpperCase();
  switch (statusUpper) {
    case 'ACTIVE':
    case 'IN_PROGRESS':
    case 'IN PROGRESS':
      return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
    case 'PLANNING':
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
    case 'COMPLETED':
    case 'COMPLETE':
      return 'bg-green-100 text-green-800 hover:bg-green-100';
    case 'ON_HOLD':
    case 'ON HOLD':
      return 'bg-red-100 text-red-800 hover:bg-red-100';
    case 'TESTING':
      return 'bg-purple-100 text-purple-800 hover:bg-purple-100';
    case 'CANCELLED':
    case 'CANCELED':
      return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'HIGH':
    case 'High':
      return 'bg-red-100 text-red-800 hover:bg-red-100';
    case 'MEDIUM':
    case 'Medium':
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
    case 'LOW':
    case 'Low':
      return 'bg-green-100 text-green-800 hover:bg-green-100';
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateString;
  }
};

export default function Projects() {
  const navigate = useNavigate();
  const { hasSubModulePermission } = useAuthStore();
  // Using Sonner toast directly
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [projectStatuses, setProjectStatuses] = useState<Array<{ name: string; color: string; id: string; isActive: boolean }>>([]);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [isViewProjectOpen, setIsViewProjectOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state (following Employee Management pattern)
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  
  // Mark as completed confirmation dialog state
  const [isMarkCompletedDialogOpen, setIsMarkCompletedDialogOpen] = useState(false);
  const [projectToComplete, setProjectToComplete] = useState<Project | null>(null);
  
  // Delete project confirmation dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  
  // Statistics state
  const [totalTasksCount, setTotalTasksCount] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);

  // Fetch projects from API with timeout
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📋 Fetching projects with myProjects=true filter...');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const response = await Promise.race([
        projectService.getProjects(),
        timeoutPromise
      ]) as any;
      
      console.log('✅ Projects API response received:', response);
      
      // Check if response has data
      if (!response || !response.data) {
        console.warn('⚠️ No projects data in response');
        setAllProjects([]);
        setProjects([]);
        setFilteredProjects([]);
        setTotalProjects(0);
        return;
      }
      
      // Transform API projects to local Project interface with populated employee data
      const transformedProjects: Project[] = response.data.map((apiProject: any) => {
        // Extract status - can be string (ID/name) or object (populated)
        let statusValue = apiProject.status;
        if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
          statusValue = statusValue.name ?? ''; // Use status name if populated
        }
        // Normalize status to uppercase for consistency
        if (statusValue && typeof statusValue === 'string') {
          // Convert common status formats to uppercase
          const statusUpper = statusValue.toUpperCase();
          const statusMap: Record<string, string> = {
            'PLANNING': 'PLANNING',
            'IN_PROGRESS': 'IN_PROGRESS',
            'IN PROGRESS': 'IN_PROGRESS',
            'ON_HOLD': 'ON_HOLD',
            'ON HOLD': 'ON_HOLD',
            'COMPLETED': 'COMPLETED',
            'COMPLETE': 'COMPLETED',
            'TESTING': 'TESTING',
            'CANCELLED': 'CANCELLED',
            'CANCELED': 'CANCELLED',
          };
          statusValue = statusMap[statusUpper] || statusUpper;
        }
        
        return {
        _id: apiProject._id, // Preserve the MongoDB _id
        id: apiProject._id || apiProject.id, // Use _id as the primary id
        name: apiProject.projectName || apiProject.name,
        client: apiProject.client,
        status: statusValue || 'PLANNING', // Use extracted and normalized status name in uppercase
        progress: apiProject.progressPercentage || 0,
        startDate: new Date(apiProject.startDate).toISOString().split('T')[0],
        endDate: new Date(apiProject.endDate).toISOString().split('T')[0],
        team: apiProject.teamMembers || [],
        priority: apiProject.priority || 'MEDIUM',
        tasks: {
          total: 0,
          completed: 0,
          inProgress: 0,
          blocked: 0,
        },
        description: apiProject.projectDescription || apiProject.description,
        createdDate: apiProject.createdAt ? new Date(apiProject.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        projectManager: apiProject.projectManager,
        projectManagerId: apiProject.projectManager?._id,
        teamMemberIds: apiProject.teamMembers?.map((member: any) => member._id) || [],
        tags: [],
        totalLoggedHours: 0,
        hoursByDay: []
        };
      });
      
      console.log('✅ Transformed projects:', transformedProjects);
      setAllProjects(transformedProjects);
      setProjects(transformedProjects);
      setFilteredProjects(transformedProjects);
      setTotalProjects(transformedProjects.length);
      console.log('✅ Total projects found:', transformedProjects.length);
    } catch (error: any) {
      console.error('❌ Error fetching projects:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      // Even on error, allow the page to render with empty state
      setAllProjects([]);
      setProjects([]);
      setFilteredProjects([]);
      setTotalProjects(0);
      
      // Only show toast if it's not a timeout or network error
      if (error.message !== 'Request timeout' && !error.message.includes('Network')) {
        toast.error("Error", {
          description: error.response?.data?.message || "Failed to fetch projects. Showing empty state.",
        });
      }
      
      setError(error.response?.data?.message || error.message || 'No projects available');
    } finally {
      console.log('✅ Finished loading projects');
      setLoading(false);
    }
  };

  // Fetch project statuses from API to get colors
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const statuses = await getStatusesByCategory('project');
        setProjectStatuses(statuses.map(s => ({ name: s.name, color: s.color, id: s.id, isActive: s.isActive })));
      } catch (error) {
        console.error('Error fetching project statuses:', error);
      }
    };
    fetchStatuses();
  }, []);

  // Filter and search functionality with support for populated employee data
  useEffect(() => {
    let filtered = [...allProjects];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => {
        // Extract status - handle both object and string formats
        let statusValue = project.status;
        if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
          statusValue = statusValue.name ?? '';
        }
        const projectStatus = statusValue ? String(statusValue).toUpperCase() : '';
        const filterStatus = statusFilter.toUpperCase();
        
        // Handle different status formats
        if (filterStatus === 'IN_PROGRESS' || filterStatus === 'IN PROGRESS') {
          return projectStatus === 'IN_PROGRESS' || projectStatus === 'IN PROGRESS' || projectStatus === 'ACTIVE';
        }
        if (filterStatus === 'ON_HOLD' || filterStatus === 'ON HOLD') {
          return projectStatus === 'ON_HOLD' || projectStatus === 'ON HOLD';
        }
        return projectStatus === filterStatus;
      });
    }

    // Apply search query
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(project => {
        // Search in basic fields
        if (
          project.name.toLowerCase().includes(lowerQuery) ||
          project.client.toLowerCase().includes(lowerQuery) ||
          getStatusName(project.status).toLowerCase().includes(lowerQuery) ||
          project.priority.toLowerCase().includes(lowerQuery)
        ) {
          return true;
        }
        
        // Search in project manager
        if (project.projectManager) {
          const pmFullName = `${project.projectManager.firstName} ${project.projectManager.lastName}`;
          if (
            pmFullName.toLowerCase().includes(lowerQuery) ||
            project.projectManager.email.toLowerCase().includes(lowerQuery) ||
            project.projectManager.employeeId.toLowerCase().includes(lowerQuery)
          ) {
            return true;
          }
        }
        
        // Search in team members
        if (project.team && project.team.length > 0) {
          return project.team.some(member => {
            const memberFullName = `${member.firstName} ${member.lastName}`;
            return (
              memberFullName.toLowerCase().includes(lowerQuery) ||
              member.email.toLowerCase().includes(lowerQuery) ||
              member.employeeId.toLowerCase().includes(lowerQuery)
            );
          });
        }
        
        return false;
      });
    }
    
    setFilteredProjects(filtered);
  }, [allProjects, statusFilter, searchQuery]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Fetch statistics (tasks and completed projects)
  const fetchStatistics = async () => {
    try {
      // Fetch all tasks to get total and pending counts
      // Use a large limit to get all tasks for accurate pending count
      const tasksResponse = await taskService.getAllTasks({ limit: 1000 });
      const allTasks = tasksResponse.data || [];
      
      // Calculate total tasks from API response
      setTotalTasksCount(tasksResponse.total || allTasks.length);
      
      // Calculate pending tasks (all tasks that are not COMPLETE/COMPLETED)
      // If we have all tasks, filter them; otherwise use total count as approximation
      if (allTasks.length > 0 && allTasks.length === tasksResponse.total) {
        // We have all tasks, can calculate accurately
        const pendingTasks = allTasks.filter((task: any) => {
          // Extract status - handle both object and string formats
          let statusValue = task.status;
        if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
          statusValue = statusValue.name ?? '';
          }
          const status = statusValue ? String(statusValue).toUpperCase() : '';
          return status !== 'COMPLETE' && status !== 'COMPLETED';
        });
        setPendingTasksCount(pendingTasks.length);
      } else {
        // If we couldn't fetch all tasks, fetch completed tasks separately to calculate pending
        try {
          const completedResponse = await taskService.getAllTasks({ status: 'COMPLETE', limit: 1000 });
          const completedCount = completedResponse.total || completedResponse.data?.length || 0;
          setPendingTasksCount(Math.max(0, tasksResponse.total - completedCount));
        } catch {
          // Fallback: if we can't get completed count, use total as pending (approximation)
          setPendingTasksCount(tasksResponse.total || 0);
        }
      }
    } catch (error: any) {
      console.error('Error fetching task statistics:', error);
      // Set defaults on error
      setTotalTasksCount(0);
      setPendingTasksCount(0);
    }
  };

  // Calculate project status counts in real-time
  const projectStatusCounts = {
    planning: allProjects.filter(p => {
      // Extract status - handle both object and string formats
      let statusValue = p.status;
        if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
          statusValue = statusValue.name ?? '';
      }
      const status = statusValue ? String(statusValue).toUpperCase() : '';
      return status === 'PLANNING';
    }).length,
    inProgress: allProjects.filter(p => {
      // Extract status - handle both object and string formats
      let statusValue = p.status;
      if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
        statusValue = statusValue.name ?? '';
      }
      const status = statusValue ? String(statusValue).toUpperCase() : '';
      return status === 'IN_PROGRESS' || status === 'IN PROGRESS' || status === 'ACTIVE';
    }).length,
    onHold: allProjects.filter(p => {
      // Extract status - handle both object and string formats
      let statusValue = p.status;
      if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
        statusValue = statusValue.name ?? '';
      }
      const status = statusValue ? String(statusValue).toUpperCase() : '';
      return status === 'ON_HOLD' || status === 'ON HOLD';
    }).length,
    completed: allProjects.filter(p => {
      // Extract status - handle both object and string formats
      let statusValue = p.status;
      if (statusValue && typeof statusValue === 'object' && 'name' in statusValue) {
        statusValue = statusValue.name ?? '';
      }
      const status = statusValue ? String(statusValue).toUpperCase() : '';
      return status === 'COMPLETED';
    }).length,
  };

  // Reset to page 1 when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // Load projects on component mount
  useEffect(() => {
    fetchProjects();
    fetchStatistics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pagination logic (following Employee Management pattern)
  useEffect(() => {
    // --- Glitch Fix: Recalculate pagination after data change ---
    setTotalProjects(filteredProjects.length);
    const totalPages = Math.ceil(filteredProjects.length / pageSize);
    const newCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

    // Pagination
    const startIndex = (newCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

    setProjects(paginatedProjects);

    // If the page was out of bounds, update it.
    if (currentPage !== newCurrentPage) {
      setCurrentPage(newCurrentPage);
    }
    // --- End of Fix ---
  }, [
    filteredProjects,
    currentPage,
    pageSize,
  ]);

  const handleProjectCreated = (createdProject: any) => {
    // Transform the created project to match local interface with populated data
    const transformedProject: Project = {
      id: createdProject.id || createdProject._id,
      name: createdProject.projectName || createdProject.name,
      client: createdProject.client,
      status: getStatusName(createdProject.status) || 'PLANNING',
      progress: createdProject.progressPercentage || 0,
      startDate: new Date(createdProject.startDate).toISOString().split('T')[0],
      endDate: new Date(createdProject.endDate).toISOString().split('T')[0],
      team: createdProject.teamMembers || [],
      priority: createdProject.priority || 'MEDIUM',
      tasks: {
        total: 0,
        completed: 0,
        inProgress: 0,
        blocked: 0,
      },
      description: createdProject.projectDescription || createdProject.description,
      createdDate: createdProject.createdAt ? new Date(createdProject.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      projectManager: createdProject.projectManager,
      projectManagerId: createdProject.projectManager?._id,
      teamMemberIds: createdProject.teamMembers?.map((member: any) => member._id) || [],
      tags: [],
      totalLoggedHours: 0,
      hoursByDay: []
    };
    
    setAllProjects(prevProjects => [transformedProject, ...prevProjects]);
    setFilteredProjects(prevProjects => [transformedProject, ...prevProjects]);
    
    // Refresh statistics when new project is created
    fetchStatistics();
    
    // Show additional notification in the main page
    toast.success("Project Added", {
      description: `"${transformedProject.name}" has been added to your project list.`,
    });
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setIsEditProjectOpen(true);
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(project);
    setIsViewProjectOpen(true);
  };

  const handleEditDialogClose = (open: boolean) => {
    setIsEditProjectOpen(open);
    if (!open) {
      setSelectedProject(null);
    }
  };

  const handleViewDialogClose = (open: boolean) => {
    setIsViewProjectOpen(open);
    if (!open) {
      setSelectedProject(null);
    }
  };

  const handleMarkAsCompleted = (project: Project) => {
    setProjectToComplete(project);
    setIsMarkCompletedDialogOpen(true);
  };

  const confirmMarkAsCompleted = async () => {
    if (!projectToComplete) return;

    try {
      // Find the status ID for "COMPLETED" status from projectStatuses
      // The status name might be "COMPLETED", "Completed", or similar
      const completedStatus = projectStatuses.find(status => {
        const statusName = status.name.toUpperCase();
        return statusName === 'COMPLETED' || statusName === 'COMPLETE';
      });

      if (!completedStatus) {
        toast.error("Error", {
          description: "Could not find 'COMPLETED' status. Please ensure statuses are loaded.",
        });
        return;
      }

      // Use the status ID (MongoDB ID) instead of the status name
      const statusId = completedStatus.id;

      // Update the project status to completed
      const updatedProject = {
        ...projectToComplete,
        status: 'COMPLETED' // For local state display
      };

      // Update the project via API using the status ID
      await projectService.updateProject(projectToComplete.id, { status: statusId as any });

      // Update local state
      setAllProjects(prevProjects => 
        prevProjects.map(p => 
          p.id === projectToComplete.id ? updatedProject : p
        )
      );
      setFilteredProjects(prevProjects => 
        prevProjects.map(p => 
          p.id === projectToComplete.id ? updatedProject : p
        )
      );

      // Refresh statistics when project is marked as completed
      fetchStatistics();
      
      toast.success("Success", {
        description: `Project "${projectToComplete.name}" has been marked as completed.`,
      });

      // Close dialog and reset state
      setIsMarkCompletedDialogOpen(false);
      setProjectToComplete(null);
    } catch (error: any) {
      console.error('Error marking project as completed:', error);
      toast.error("Error", {
        description: error.response?.data?.message || "Failed to mark project as completed. Please try again.",
      });
    }
  };

  const cancelMarkAsCompleted = () => {
    setIsMarkCompletedDialogOpen(false);
    setProjectToComplete(null);
  };

  const handleDeleteProject = (project: Project) => {
    setProjectToDelete(project);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      await projectService.deleteProject(projectToDelete.id);

      // Remove from local state
      setAllProjects(prevProjects => prevProjects.filter(p => p.id !== projectToDelete.id));
      setFilteredProjects(prevProjects => prevProjects.filter(p => p.id !== projectToDelete.id));
      
      // Update total count
      setTotalProjects(prev => prev - 1);

      // Refresh statistics when project is deleted
      fetchStatistics();

      toast.success("Success", {
        description: `Project "${projectToDelete.name}" has been deleted successfully.`,
      });

      // Close dialog and reset state
      setIsDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error: any) {
      console.error('Error deleting project:', error);
      toast.error("Error", {
        description: error.response?.data?.message || "Failed to delete project. Please try again.",
      });
    }
  };

  const cancelDeleteProject = () => {
    setIsDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleProjectUpdated = (updatedProject: any) => {
    // Transform the updated project to match local interface with populated data
    const transformedProject: Project = {
      id: updatedProject.id || updatedProject._id,
      name: updatedProject.projectName || updatedProject.name,
      client: updatedProject.client,
      status: getStatusName(updatedProject.status) || 'PLANNING',
      progress: updatedProject.progressPercentage || 0,
      startDate: new Date(updatedProject.startDate).toISOString().split('T')[0],
      endDate: new Date(updatedProject.endDate).toISOString().split('T')[0],
      team: updatedProject.teamMembers || [],
      priority: updatedProject.priority || 'MEDIUM',
      tasks: {
        total: 0,
        completed: 0,
        inProgress: 0,
        blocked: 0,
      },
      description: updatedProject.projectDescription || updatedProject.description,
      createdDate: updatedProject.createdAt ? new Date(updatedProject.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      projectManager: updatedProject.projectManager,
      projectManagerId: updatedProject.projectManager?._id,
      teamMemberIds: updatedProject.teamMembers?.map((member: any) => member._id) || [],
      tags: [],
      totalLoggedHours: 0,
      hoursByDay: []
    };
    
    setAllProjects(prevProjects => 
      prevProjects.map(project => 
        project.id === transformedProject.id ? transformedProject : project
      )
    );
    setFilteredProjects(prevProjects => 
      prevProjects.map(project => 
        project.id === transformedProject.id ? transformedProject : project
      )
    );
    
    setSelectedProject(null);
    setIsEditProjectOpen(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
            <p className="text-muted-foreground">
              Manage and track your projects
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading projects...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
            <p className="text-muted-foreground">
              Manage and track your projects
            </p>
          </div>
          <Button onClick={() => fetchProjects()} variant="outline">
            Retry
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Projects</h3>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchProjects()}>
              <Plus className="h-4 w-4 mr-2" />
              Retry Loading
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">
            Manage and track your projects
          </p>
        </div>
        {hasSubModulePermission('project_management', 'project', 'create') && (
          <Button onClick={() => setIsCreateProjectOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Project
          </Button>
        )}
      </div>

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Total Projects</p>
                <p className="text-3xl font-bold text-gray-900">{allProjects.length}</p>
                <p className="text-xs text-muted-foreground mt-1">Total projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <ListTodo className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Total Tasks</p>
                <p className="text-3xl font-bold text-gray-900">{totalTasksCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Across all projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Pending Tasks</p>
                <p className="text-3xl font-bold text-gray-900">{pendingTasksCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Awaiting completion</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Planning</p>
                <p className="text-3xl font-bold text-gray-900">{projectStatusCounts.planning}</p>
                <p className="text-xs text-muted-foreground mt-1">Projects in planning</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">In Progress</p>
                <p className="text-3xl font-bold text-gray-900">{projectStatusCounts.inProgress}</p>
                <p className="text-xs text-muted-foreground mt-1">Active projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Completed</p>
                <p className="text-3xl font-bold text-gray-900">{projectStatusCounts.completed}</p>
                <p className="text-xs text-muted-foreground mt-1">Finished projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Project List</CardTitle>
              <CardDescription>
                {searchQuery ? 
                  `Found ${filteredProjects.length} of ${allProjects.length} projects` : 
                  `View and manage your projects`
                }
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search projects..." 
                  className="pl-8" 
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {projectStatuses
                    .filter(status => status.isActive)
                    .map((status) => (
                      <SelectItem key={status.id} value={status.name.toUpperCase()}>
                        {status.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Select
                value={pageSize.toString()}
                onValueChange={(v) => setPageSize(Number(v))}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Rows per page" />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                {/* <TableHead>Progress</TableHead> */}
                <TableHead>Priority</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Team</TableHead>
                {/* <TableHead>Tasks</TableHead> */}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    {searchQuery ? 'No projects found matching your search.' : 'No projects available.'}
                  </TableCell>
                </TableRow>
              ) : (
                projects.map((project) => {
                  const seenMemberKeys = new Set<string>();
                  const uniqueTeamMembers = project.team.filter((member, index) => {
                    const key = member._id || member.email || `member-${index}`;
                    if (seenMemberKeys.has(key)) {
                      return false;
                    }
                    seenMemberKeys.add(key);
                    return true;
                  });

                  const displayMembers = uniqueTeamMembers.slice(0, 3);
                  const remainingCount = Math.max(uniqueTeamMembers.length - displayMembers.length, 0);

                  return (
                    <TableRow key={project.id}>
                      <TableCell className="font-medium">
                        <button
                          onClick={() => navigate(`/projects/${project.id}`, { state: { project } })}
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                        >
                          {project.name}
                        </button>
                      </TableCell>
                      <TableCell>{project.client}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(project.status, projectStatuses)}>
                          {getStatusName(project.status)}
                        </Badge>
                      </TableCell>
                      {/* <TableCell>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-blue-600 h-2.5 rounded-full"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {project.progress}%
                        </span>
                      </TableCell> */}
                      <TableCell>
                        <Badge className={getPriorityColor(project.priority)}>
                          {project.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(project.startDate)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(project.endDate)}
                      </TableCell>
                      <TableCell>
                        <div className="flex -space-x-2 items-center">
                          {displayMembers.map((member, index) => {
                            const imageUrl = getProfileImageUrl(member.profilePicture);
                            const initials = getInitials(member.firstName, member.lastName, member.email) || 'NA';
                            const fullName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
                            const titleText = fullName || member.email || 'Team member';

                            return (
                              <div
                                key={member._id || member.email || `member-${index}`}
                                className="relative w-8 h-8 rounded-full border-2 border-white bg-gray-200 text-xs font-medium flex items-center justify-center overflow-hidden"
                                title={titleText}
                              >
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={fullName || member.email || 'Team member'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <span className={`${imageUrl ? 'hidden' : ''}`}>
                                  {initials}
                                </span>
                              </div>
                            );
                          })}
                          {remainingCount > 0 && (
                            <div className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-medium">
                              +{remainingCount}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      {/* <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
                            {project.tasks.completed}/{project.tasks.total} completed
                          </div>
                          <div className="flex items-center text-xs">
                            <Clock className="h-3 w-3 mr-1 text-blue-500" />
                            {project.tasks.inProgress} in progress
                          </div>
                          {project.tasks.blocked > 0 && (
                            <div className="flex items-center text-xs">
                              <AlertCircle className="h-3 w-3 mr-1 text-red-500" />
                              {project.tasks.blocked} blocked
                            </div>
                          )}
                        </div>
                      </TableCell> */}
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {hasSubModulePermission('project_management', 'project', 'edit') && (
                              <DropdownMenuItem onClick={() => handleEditProject(project)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Project
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleViewProject(project)}>
                              <FileText className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {getStatusName(project.status) !== 'COMPLETED' && hasSubModulePermission('project_management', 'project', 'edit') && (
                              <DropdownMenuItem onClick={() => handleMarkAsCompleted(project)}>
                                <Check className="h-4 w-4 mr-2" />
                                Mark as Completed
                              </DropdownMenuItem>
                            )}
                            {hasSubModulePermission('project_management', 'project', 'delete') && (
                              <DropdownMenuItem 
                                onClick={() => handleDeleteProject(project)}
                                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Project
                              </DropdownMenuItem>
                            )}
                            {/* <DropdownMenuItem>
                              <Users className="h-4 w-4 mr-2" />
                              Manage Team
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Calendar className="h-4 w-4 mr-2" />
                              Update Timeline
                            </DropdownMenuItem> */}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {!loading && projects.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-4">
              <div className="text-sm text-gray-500">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, totalProjects)} of{" "}
                {totalProjects} entries
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="h-8 px-3 text-sm"
                >
                  Previous
                </Button>
                <Button
                  variant="default"
                  className="h-8 w-8 p-0 text-sm bg-[#000000] hover:bg-[#000000] text-white"
                  disabled
                >
                  {currentPage}
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(Math.ceil(totalProjects / pageSize), prev + 1))
                  }
                  disabled={currentPage === Math.ceil(totalProjects / pageSize)}
                  className="h-8 px-3 text-sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateProjectForm
        open={isCreateProjectOpen}
        onOpenChange={setIsCreateProjectOpen}
        onProjectCreated={handleProjectCreated}
      />

      <CreateProjectForm
        open={isEditProjectOpen}
        onOpenChange={handleEditDialogClose}
        onProjectCreated={handleProjectUpdated}
        mode="edit"
        selectedProject={selectedProject}
        initialData={selectedProject ? {
          projectName: selectedProject.name,
          client: selectedProject.client,
          description: selectedProject.description || '',
          status: typeof selectedProject.status === 'object' && selectedProject.status !== null
            ? (selectedProject.status.name ?? '')
            : (selectedProject.status || ''),
          priority: selectedProject.priority,
          startDate: selectedProject.startDate,
          endDate: selectedProject.endDate,
          projectManager: selectedProject.projectManagerId || '',
          teamMembers: selectedProject.teamMemberIds || [],
          budget: 0
        } : null}
      />

      <ViewProjectDialog
        project={selectedProject}
        open={isViewProjectOpen}
        onOpenChange={handleViewDialogClose}
      />

      {/* Mark as Completed Confirmation Dialog */}
      <AlertDialog open={isMarkCompletedDialogOpen} onOpenChange={setIsMarkCompletedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Project as Completed</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark the project "{projectToComplete?.name}" as completed? 
              This action will change the project status and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelMarkAsCompleted}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarkAsCompleted}>
              Mark as Completed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Project Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the project "{projectToDelete?.name}"? 
              <br /><br />
              This action cannot be undone and will permanently remove the project and all associated data including tasks, timesheets, and other related information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteProject}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteProject}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 